import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PiArrowLeftDuotone,
  PiCheckCircleDuotone,
  PiEyeDuotone,
  PiTrashDuotone,
  PiWarningDuotone,
} from 'react-icons/pi';
import { createEventType, getEventType, updateEventType } from '../api/eventTypeService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Input from '../shared/common/Input';
import Select from '../shared/common/Select';
import EmptyState from '../shared/common/EmptyState';
import {
  ParticipantKind,
  PARTICIPANT_KIND_OPTIONS,
  OutcomeScheme,
  DEFAULT_WIN_METHODS,
  WIN_METHOD_LABELS,
  EMPTY_EVENT_TYPE,
  ScoringRuleType,
} from '../models/eventType';
import {
  outcomeSchemeLabel,
  outcomeSchemeDescription,
  outcomeSchemeIcon,
  getOutcomeSchemeOptions,
  isDrawPolicyRequired,
  isWinMethodsRequired,
  isIndividualScoreParamsRequired,
  isCustomScheme,
} from '../shared/business/eventType/outcomeSchemeHelpers';
import {
  generateOutcomes,
  getApplicableScoringRuleOptions,
  getDefaultDrawPolicyForScheme,
  getDrawPolicyOptions,
  formatAllowedOutcomes,
} from '../shared/business/eventType/eventTypeCapabilities';

const NAME_REQUIRED_ERROR = 'Название обязательно';
const SLUG_REQUIRED_ERROR = 'Slug обязателен';
const SLUG_FORMAT_ERROR = 'Slug: только строчные латинские буквы, цифры и дефисы';
const SLUG_FORMAT = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;
const PARTICIPANT_MIN_ERROR = 'Минимум участников должен быть ≥ 2';
const PARTICIPANT_MAX_ERROR = 'Максимум участников должен быть ≥ минимума';
const WIN_METHOD_REQUIRED_ERROR = 'Добавьте хотя бы один способ победы';
const INDIVIDUAL_SCORE_RANGE_ERROR = 'Максимум баллов должен быть больше минимума';
const SCORING_RULES_REQUIRED_ERROR = 'Выберите хотя бы одно scoring-правило';
const NEW_WIN_METHOD_LABEL = 'Новый способ';
const CREATE_ERROR = 'Не удалось создать тип события';
const UPDATE_ERROR = 'Не удалось обновить тип события';
const LOAD_ERROR = 'Не удалось загрузить тип события';
const PREVIEW_NO_OUTCOMES = 'Для произвольных исходов список формируется вручную при создании события';

export default function EventTypeConstructorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState({ ...EMPTY_EVENT_TYPE });
  const [newWinMethod, setNewWinMethod] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState('');

  // Загрузка для редактирования
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getEventType(id);
        if (!cancelled) {
          setForm(mapDtoToForm(data));
        }
      } catch {
        if (!cancelled) setLoadError(LOAD_ERROR);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, isEditMode]);

  // При смене OutcomeScheme обновляем applicableScoringRules и DrawPolicy
  const handleSchemeChange = useCallback((newScheme) => {
    setForm((prev) => {
      const updated = { ...prev, outcomeScheme: newScheme };
      updated.applicableScoringRules = getApplicableScoringRuleOptions(newScheme).map((r) => r.value);
      if (isDrawPolicyRequired(newScheme)) {
        updated.scoreDrawPolicy = prev.scoreDrawPolicy || getDefaultDrawPolicyForScheme(newScheme);
      } else {
        updated.scoreDrawPolicy = null;
      }
      if (!isWinMethodsRequired(newScheme)) {
        updated.winMethods = [...DEFAULT_WIN_METHODS];
      }
      return updated;
    });
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Валидация
  const validate = useCallback(() => {
    const errs = {};
    if (!form.name.trim()) errs.name = NAME_REQUIRED_ERROR;
    if (!form.slug.trim()) errs.slug = SLUG_REQUIRED_ERROR;
    else if (!SLUG_FORMAT.test(form.slug)) errs.slug = SLUG_FORMAT_ERROR;

    const minCount = Number(form.participantCountMin);
    const maxCount = Number(form.participantCountMax);
    if (minCount < 2) errs.participantCountMin = PARTICIPANT_MIN_ERROR;
    if (maxCount < minCount) errs.participantCountMax = PARTICIPANT_MAX_ERROR;

    if (isWinMethodsRequired(form.outcomeScheme) && (!form.winMethods || form.winMethods.length === 0)) {
      errs.winMethods = WIN_METHOD_REQUIRED_ERROR;
    }
    if (isIndividualScoreParamsRequired(form.outcomeScheme)) {
      const scoreMin = Number(form.individualScoreMin);
      const scoreMax = Number(form.individualScoreMax);
      if (scoreMax <= scoreMin) errs.individualScoreMax = INDIVIDUAL_SCORE_RANGE_ERROR;
    }
    if (!form.applicableScoringRules || form.applicableScoringRules.length === 0) {
      errs.applicableScoringRules = SCORING_RULES_REQUIRED_ERROR;
    }
    return errs;
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrors({});
    try {
      const payload = mapFormToPayload(form);
      if (isEditMode) {
        await updateEventType(id, payload);
      } else {
        await createEventType(payload);
      }
      navigate('/event-types');
    } catch {
      setErrors({ global: isEditMode ? UPDATE_ERROR : CREATE_ERROR });
    } finally {
      setLoading(false);
    }
  };

  // Win methods
  const addWinMethod = useCallback(() => {
    const trimmed = newWinMethod.trim().toUpperCase();
    if (!trimmed || form.winMethods.includes(trimmed)) return;
    setForm((prev) => ({ ...prev, winMethods: [...prev.winMethods, trimmed] }));
    setNewWinMethod('');
  }, [newWinMethod, form.winMethods]);

  const removeWinMethod = useCallback((method) => {
    setForm((prev) => ({ ...prev, winMethods: prev.winMethods.filter((m) => m !== method) }));
  }, []);

  // Scoring rules toggle
  const toggleScoringRule = useCallback((ruleType) => {
    setForm((prev) => {
      const rules = prev.applicableScoringRules || [];
      const next = rules.includes(ruleType)
        ? rules.filter((r) => r !== ruleType)
        : [...rules, ruleType];
      return { ...prev, applicableScoringRules: next };
    });
  }, []);

  // Предпросмотр исходов
  const previewOutcomes = useMemo(() => generateOutcomes(form), [form]);
  const schemeOptions = useMemo(() => getOutcomeSchemeOptions(), []);
  const drawPolicyOptions = useMemo(() => getDrawPolicyOptions(), []);
  const applicableRules = useMemo(
    () => getApplicableScoringRuleOptions(form.outcomeScheme),
    [form.outcomeScheme]
  );

  if (initialLoading) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 720 }}>
        <EmptyState title="Загрузка…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 720 }}>
        <EmptyState title="Ошибка" description={loadError} />
        <Button variant="ghost" onClick={() => navigate('/event-types')} style={{ marginTop: 'var(--bm-space-md)' }}>
          Назад
        </Button>
      </div>
    );
  }

  const isBuiltIn = form.isBuiltIn;

  return (
    <div className="bm-animate-in" style={{ maxWidth: 720 }}>
      <Button
        variant="ghost"
        size="sm"
        icon={<PiArrowLeftDuotone size={16} />}
        onClick={() => navigate('/event-types')}
        style={{ marginBottom: 'var(--bm-space-lg)' }}
      >
        Назад к типам событий
      </Button>

      <h1 className="bm-h2" style={{ marginBottom: 'var(--bm-space-xl)' }}>
        {isEditMode ? 'Редактировать тип события' : 'Новый тип события'}
      </h1>

      <Card>
        {errors.global && (
          <div className="bm-auth-error" style={{ marginBottom: 'var(--bm-space-md)' }}>
            {errors.global}
          </div>
        )}

        {isBuiltIn && (
          <div className="bm-scoring-builder__warning" style={{ marginBottom: 'var(--bm-space-md)' }}>
            <PiWarningDuotone size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Встроенный тип нельзя редактировать. Можно только просмотреть.
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          {/* Основная информация */}
          <Input
            label="Название *"
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Например: Формула-1"
            error={errors.name}
            disabled={isBuiltIn}
          />
          <Input
            label="Slug *"
            type="text"
            value={form.slug}
            onChange={(e) => updateField('slug', e.target.value)}
            placeholder="Например: formula-1"
            error={errors.slug}
            disabled={isBuiltIn}
          />
          <Input
            label="Описание"
            as="textarea"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Краткое описание типа события"
            rows={2}
            disabled={isBuiltIn}
          />

          {/* Тип участника */}
          <Select
            label="Тип участника"
            value={form.participantKind}
            onChange={(val) => updateField('participantKind', val)}
            options={PARTICIPANT_KIND_OPTIONS}
            disabled={isBuiltIn}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--bm-space-md)' }}>
            <Input
              label="Минимум участников"
              type="number"
              value={String(form.participantCountMin)}
              onChange={(e) => updateField('participantCountMin', Number(e.target.value))}
              min="2"
              error={errors.participantCountMin}
              disabled={isBuiltIn}
            />
            <Input
              label="Максимум участников"
              type="number"
              value={String(form.participantCountMax)}
              onChange={(e) => updateField('participantCountMax', Number(e.target.value))}
              min="2"
              error={errors.participantCountMax}
              disabled={isBuiltIn}
            />
          </div>
          <Input
            label="Метка участника"
            type="text"
            value={form.participantLabel}
            onChange={(e) => updateField('participantLabel', e.target.value)}
            placeholder="Например: команда, боец, гонщик"
            disabled={isBuiltIn}
          />

          {/* Схема исходов */}
          <Select
            label="Схема исходов"
            value={form.outcomeScheme}
            onChange={handleSchemeChange}
            options={schemeOptions}
            disabled={isBuiltIn}
          />
          <p className="bm-muted" style={{ marginTop: 'calc(var(--bm-space-sm) * -1)' }}>
            {outcomeSchemeIcon(form.outcomeScheme)} {outcomeSchemeDescription(form.outcomeScheme)}
          </p>

          {/* SCORE_BASED → DrawPolicy */}
          {isDrawPolicyRequired(form.outcomeScheme) && (
            <Select
              label="Политика ничьей"
              value={form.scoreDrawPolicy || 'DRAW_ALLOWED'}
              onChange={(val) => updateField('scoreDrawPolicy', val)}
              options={drawPolicyOptions}
              disabled={isBuiltIn}
            />
          )}

          {/* WINNER_WITH_METHOD → winMethods */}
          {isWinMethodsRequired(form.outcomeScheme) && (
            <div>
              <div className="bm-label" style={{ marginBottom: 'var(--bm-space-xs)' }}>
                Способы победы *
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-sm)', marginBottom: 'var(--bm-space-sm)' }}>
                {form.winMethods.map((method) => (
                  <span key={method} className="bm-friend-chip">
                    <span>{WIN_METHOD_LABELS[method] || method}</span>
                    {!isBuiltIn && (
                      <button
                        type="button"
                        onClick={() => removeWinMethod(method)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--bm-accent-red)' }}
                        aria-label={`Удалить ${method}`}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!isBuiltIn && (
                <div style={{ display: 'flex', gap: 'var(--bm-space-sm)', alignItems: 'flex-end' }}>
                  <Input
                    label={NEW_WIN_METHOD_LABEL}
                    type="text"
                    value={newWinMethod}
                    onChange={(e) => setNewWinMethod(e.target.value)}
                    placeholder="Например: DQ"
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    icon={null}
                    onClick={addWinMethod}
                    disabled={!newWinMethod.trim()}
                  >
                    Добавить
                  </Button>
                </div>
              )}
              {errors.winMethods && <span className="bm-input-error-msg">{errors.winMethods}</span>}
            </div>
          )}

          {/* INDIVIDUAL_SCORE → параметры балльной системы */}
          {isIndividualScoreParamsRequired(form.outcomeScheme) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--bm-space-md)' }}>
              <Input
                label="Мин. балл"
                type="number"
                value={String(form.individualScoreMin)}
                onChange={(e) => updateField('individualScoreMin', Number(e.target.value))}
                disabled={isBuiltIn}
              />
              <Input
                label="Макс. балл"
                type="number"
                value={String(form.individualScoreMax)}
                onChange={(e) => updateField('individualScoreMax', Number(e.target.value))}
                error={errors.individualScoreMax}
                disabled={isBuiltIn}
              />
              <Input
                label="Точность (знаки)"
                type="number"
                value={String(form.individualScorePrecision)}
                onChange={(e) => updateField('individualScorePrecision', Number(e.target.value))}
                min="0"
                max="3"
                disabled={isBuiltIn}
              />
            </div>
          )}

          {/* Применимые scoring-правила */}
          <div>
            <div className="bm-label" style={{ marginBottom: 'var(--bm-space-xs)' }}>
              Применимые scoring-правила *
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-sm)' }}>
              {applicableRules.map((rule) => (
                <label
                  key={rule.value}
                  className={`bm-check-card${(form.applicableScoringRules || []).includes(rule.value) ? ' bm-check-card--checked' : ''}`}
                  style={{ padding: 'var(--bm-space-xs) var(--bm-space-sm)', cursor: isBuiltIn ? 'default' : 'pointer' }}
                >
                  <input
                    className="bm-check-card__input"
                    type="checkbox"
                    checked={(form.applicableScoringRules || []).includes(rule.value)}
                    onChange={() => !isBuiltIn && toggleScoringRule(rule.value)}
                    disabled={isBuiltIn}
                  />
                  <span className="bm-check-card__box" aria-hidden="true" />
                  <span className="bm-check-card__content">
                    <span className="bm-check-card__name">{rule.label}</span>
                  </span>
                </label>
              ))}
            </div>
            {errors.applicableScoringRules && <span className="bm-input-error-msg">{errors.applicableScoringRules}</span>}
          </div>

          {/* Предпросмотр исходов */}
          <div style={{ background: 'var(--bm-bg-secondary)', borderRadius: 'var(--bm-radius-md)', padding: 'var(--bm-space-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-xs)', marginBottom: 'var(--bm-space-sm)' }}>
              <PiEyeDuotone size={16} />
              <span className="bm-label">Предпросмотр исходов</span>
            </div>
            {isCustomScheme(form.outcomeScheme) ? (
              <p className="bm-muted">{PREVIEW_NO_OUTCOMES}</p>
            ) : previewOutcomes.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-xs)' }}>
                {previewOutcomes.map((outcome) => (
                  <span
                    key={outcome}
                    style={{
                      background: 'var(--bm-accent-blue-bg, #e8f0fe)',
                      color: 'var(--bm-accent-blue, #1a73e8)',
                      padding: '2px 8px',
                      borderRadius: 'var(--bm-radius-sm)',
                      fontSize: 'var(--bm-text-sm)',
                      fontWeight: 500,
                    }}
                  >
                    {outcome}
                  </span>
                ))}
              </div>
            ) : (
              <p className="bm-muted">—</p>
            )}
          </div>

          {/* Кнопки */}
          {!isBuiltIn && (
            <div style={{ display: 'flex', gap: 'var(--bm-space-sm)', justifyContent: 'flex-end', paddingTop: 'var(--bm-space-sm)' }}>
              <Button variant="ghost" type="button" onClick={() => navigate('/event-types')}>
                Отмена
              </Button>
              <Button variant="primary" type="submit" loading={loading}>
                {isEditMode ? 'Сохранить' : 'Создать тип'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}

// ===================================================================================================================
// = Implementation
// ===================================================================================================================

function mapDtoToForm(dto) {
  return {
    name: dto.name || '',
    slug: dto.slug || '',
    description: dto.description || '',
    participantKind: dto.participantKind || 'TEAM',
    participantCountMin: dto.participantCountMin ?? 2,
    participantCountMax: dto.participantCountMax ?? 2,
    participantLabel: dto.participantLabel || '',
    outcomeScheme: dto.outcomeScheme || 'SCORE_BASED',
    scoreDrawPolicy: dto.scoreDrawPolicy || null,
    winMethods: dto.winMethods?.length ? [...dto.winMethods] : [...DEFAULT_WIN_METHODS],
    individualScoreMin: dto.individualScoreMin ?? 0,
    individualScoreMax: dto.individualScoreMax ?? 100,
    individualScorePrecision: dto.individualScorePrecision ?? 1,
    applicableScoringRules: dto.applicableScoringRules?.length
      ? [...dto.applicableScoringRules]
      : getApplicableScoringRuleOptions(dto.outcomeScheme || 'SCORE_BASED').map((r) => r.value),
    defaultScoringProfileId: dto.defaultScoringProfileId || null,
    isBuiltIn: dto.isBuiltIn || false,
  };
}

function mapFormToPayload(form) {
  const payload = {
    name: form.name.trim(),
    slug: form.slug.trim(),
    description: form.description.trim(),
    participantKind: form.participantKind,
    participantCountMin: Number(form.participantCountMin),
    participantCountMax: Number(form.participantCountMax),
    participantLabel: form.participantLabel.trim(),
    outcomeScheme: form.outcomeScheme,
    applicableScoringRules: form.applicableScoringRules,
  };

  if (isDrawPolicyRequired(form.outcomeScheme)) {
    payload.scoreDrawPolicy = form.scoreDrawPolicy;
  }
  if (isWinMethodsRequired(form.outcomeScheme)) {
    payload.winMethods = form.winMethods;
  }
  if (isIndividualScoreParamsRequired(form.outcomeScheme)) {
    payload.individualScoreMin = Number(form.individualScoreMin);
    payload.individualScoreMax = Number(form.individualScoreMax);
    payload.individualScorePrecision = Number(form.individualScorePrecision);
  }
  if (form.defaultScoringProfileId) {
    payload.defaultScoringProfileId = form.defaultScoringProfileId;
  }

  return payload;
}
